import { createContext, useContext, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { resolveError } from "./resolveError.js";
import { ErrorDialog } from "./ErrorDialog.js";

type ShowError = (err: unknown) => void;
const ErrorContext = createContext<ShowError | null>(null);

/**
 * App-wide error surface: mounts ONE ErrorDialog and exposes `useShowError()`. Any operation failure calls
 * `showError(err)` (e.g. `mutate(vars, { onError: showError })`) — the error is resolved to friendly copy + optional
 * detail and shown in the shared dialog. Field-level validation stays inline in the forms; this is for operation
 * (mutation/action) failures.
 */
export function ErrorProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  // The RAW error is what we hold, not its resolved copy: resolving at render means a language switch with the
  // dialog open re-renders it in the new language, where resolved-at-throw would freeze the old one on screen.
  const [raw, setRaw] = useState<{ err: unknown } | null>(null);
  const showError: ShowError = (err) => setRaw({ err });
  return (
    <ErrorContext.Provider value={showError}>
      {children}
      <ErrorDialog error={raw ? resolveError(raw.err, t) : null} onClose={() => setRaw(null)} />
    </ErrorContext.Provider>
  );
}

/** The app-wide "show an error dialog" function. Pass a thrown mutation error (or any error). */
export function useShowError(): ShowError {
  const ctx = useContext(ErrorContext);
  if (!ctx) throw new Error("useShowError must be used within <ErrorProvider>");
  return ctx;
}
